import * as THREE from "three";
import { CONSTRAINT_LABEL_CLASS, JOINT_LABEL_CLASS } from "./labelUtils";

/** Pivot proximity (m) to treat labels as sharing one attachment point. */
export const LABEL_STACK_CLUSTER_EPS_METERS = 0.018;

/** Lift above pivot for constraint/tendon/equality marker spheres (~12 mm radius). Singleton + stacked column baseline. */
export const CONSTRAINT_MARKER_LABEL_LIFT_LOCAL_METERS = 0.017;

/** Lift above pivot for joint viz labels (~20 mm default center sphere — slightly higher than constraint markers). */
export const JOINT_MARKER_LABEL_LIFT_LOCAL_METERS = 0.028;

/** Same as constraint singleton lift — first stacked row aligns with equality/tendon baseline. */
export const LABEL_STACK_COLUMN_BASE_LIFT_METERS = CONSTRAINT_MARKER_LABEL_LIFT_LOCAL_METERS;

/** Vertical separation between stacked labels (world meters), toward bottom of viewport. */
export const LABEL_STACK_LINE_STEP_METERS = 0.03;

const DEFAULT_JOINT_BASE_LOCAL = Object.freeze(
  new THREE.Vector3(0, JOINT_MARKER_LABEL_LIFT_LOCAL_METERS, 0)
);
const DEFAULT_CONSTRAINT_BASE_LOCAL = Object.freeze(
  new THREE.Vector3(0, CONSTRAINT_MARKER_LABEL_LIFT_LOCAL_METERS, 0)
);

const _worldAnchor = new THREE.Vector3();
const _spreadWorld = new THREE.Vector3();
/** Camera-local −Y in world space → downward on CSS2D viewport. */
const _screenDownWorld = new THREE.Vector3();
const _columnStart = new THREE.Vector3();
const _liftAxis = new THREE.Vector3();

function setScreenDownWorldDirection(camera, target = _screenDownWorld) {
  target.set(0, -1, 0).applyQuaternion(camera.quaternion).normalize();
}

/** Same CSS2D anchoring as constraint + joint markers (label grows to the right of the pivot). */
export function applyMarkerLabelCss2dLayout(css2dObject) {
  if (!css2dObject?.center || !css2dObject.element) return;
  css2dObject.center.set(0, 0.5);
  css2dObject.element.style.marginLeft = "8px";
}

/**
 * Labels that share a marker pivot: stack in a tight vertical column (next line directly under the
 * previous in screen space). Singletons keep their authored local offset only. CSS2DRenderer also
 * sorts by camera distance so overlaps are not permanently hidden behind one another.
 */
export function applyCoincidentLabelSpread(scene, camera, options = {}) {
  if (!scene || !camera || typeof scene.traverse !== "function") return;

  const eps = Number(options.epsilonMeters ?? LABEL_STACK_CLUSTER_EPS_METERS);
  const lineStepOpt = options.lineStepMeters ?? LABEL_STACK_LINE_STEP_METERS;
  const liftOpt = options.columnBaseLiftMeters ?? LABEL_STACK_COLUMN_BASE_LIFT_METERS;
  const epsilon = Number.isFinite(eps) && eps > 0 ? eps : LABEL_STACK_CLUSTER_EPS_METERS;
  const lineStep =
    Number.isFinite(Number(lineStepOpt)) && Number(lineStepOpt) > 0
      ? Number(lineStepOpt)
      : LABEL_STACK_LINE_STEP_METERS;
  const columnLift =
    Number.isFinite(Number(liftOpt)) && Number(liftOpt) >= 0
      ? Number(liftOpt)
      : LABEL_STACK_COLUMN_BASE_LIFT_METERS;

  /** @type {{ obj: THREE.Object3D, parent: THREE.Object3D, baseLocal: THREE.Vector3, sortKey: string, pivotWorld: THREE.Vector3 }[]} */
  const entries = [];

  scene.traverse((obj) => {
    if (!obj?.isCSS2DObject || !obj.visible || !obj.element || !obj.parent) return;

    const cls = obj.element.className;
    if (cls !== JOINT_LABEL_CLASS && cls !== CONSTRAINT_LABEL_CLASS) return;

    let baseLocal = obj.userData.stackSpreadBaseLocal;
    if (!(baseLocal instanceof THREE.Vector3)) {
      baseLocal =
        cls === JOINT_LABEL_CLASS ? DEFAULT_JOINT_BASE_LOCAL : DEFAULT_CONSTRAINT_BASE_LOCAL;
    }

    let sortKey = obj.userData.stackSpreadSortKey;
    if (typeof sortKey !== "string" || !sortKey.length) {
      if (cls === JOINT_LABEL_CLASS) {
        sortKey = `joint:${obj.parent?.userData?.jointUuid ?? ""}`;
      } else {
        sortKey = `constraint:${obj.userData?.constraintMarkerKey ?? ""}`;
      }
    }

    const pivotWorld = new THREE.Vector3().setFromMatrixPosition(obj.parent.matrixWorld);
    entries.push({
      obj,
      parent: obj.parent,
      baseLocal,
      sortKey,
      pivotWorld,
    });
  });

  if (entries.length === 0) return;

  /** @type {typeof entries[]} */
  const clusters = [];
  for (const e of entries) {
    let placed = false;
    for (const c of clusters) {
      const nearMember = c.some((other) => other.pivotWorld.distanceTo(e.pivotWorld) < epsilon);
      if (nearMember) {
        c.push(e);
        placed = true;
        break;
      }
    }
    if (!placed) clusters.push([e]);
  }

  setScreenDownWorldDirection(camera, _screenDownWorld);

  for (const cluster of clusters) {
    cluster.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    if (cluster.length === 1) {
      const { obj, parent, baseLocal } = cluster[0];
      _worldAnchor.copy(baseLocal);
      parent.localToWorld(_worldAnchor);
      obj.position.copy(_worldAnchor);
      parent.worldToLocal(obj.position);
      continue;
    }

    const pivotWorld = cluster[0].pivotWorld;
    _liftAxis.copy(camera.up).normalize();
    _columnStart.copy(pivotWorld).addScaledVector(_liftAxis, columnLift);

    cluster.forEach((e, index) => {
      _spreadWorld.copy(_columnStart).addScaledVector(_screenDownWorld, index * lineStep);
      e.obj.position.copy(_spreadWorld);
      e.parent.worldToLocal(e.obj.position);
    });
  }
}
